import { useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import type { CurrencyCode } from '../../../types';
import { CURRENCIES } from '../../../lib/currencies';
import { cn } from '../../../lib/utils';

interface CurrencySelectorProps {
  value: CurrencyCode;
  onChange: (code: CurrencyCode) => void;
  filter?: 'all' | 'fiat' | 'crypto';
  compact?: boolean;
}

export function CurrencySelector({ value, onChange, filter = 'all', compact }: CurrencySelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = CURRENCIES.find((c) => c.code === value) ?? CURRENCIES[0];

  const list = CURRENCIES.filter(
    (c) =>
      (filter === 'all' || c.type === filter) &&
      (c.code.toLowerCase().includes(query.toLowerCase()) || c.name.toLowerCase().includes(query.toLowerCase())),
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-2 bg-[#F5F5F5] rounded-full font-semibold hover:bg-[#E5E5E5] transition-colors',
          compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2',
        )}
      >
        <span>{current.flag}</span>
        {current.code}
        <ChevronDown size={14} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-2 w-64 bg-white rounded-2xl border border-[#E5E5E5] shadow-xl p-2 max-h-72 overflow-hidden flex flex-col">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search currency"
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#F5F5F5] rounded-xl focus:outline-none"
              />
            </div>
            <div className="overflow-y-auto">
              {list.map((c) => (
                <button
                  key={c.code}
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left text-sm hover:bg-[#F5F5F5]',
                    c.code === value && 'bg-[#F5F5F5]',
                  )}
                >
                  <span className="text-lg">{c.flag}</span>
                  <span className="font-semibold">{c.code}</span>
                  <span className="text-[#999999] truncate">{c.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
